/**
 * Pure Brain matrix helpers — selection → cell → strategy rows.
 * One source of truth: stratRowsFor / comboRowsFor (same list the panel renders).
 * Cell badges MUST count via those builders — never trust raw API n_strat alone.
 */

export const ASSETS = ['btc', 'eth']
export const TFS = ['5m', '15m']

/** Catalog paper pairs — ВКЛ without live heartbeat. No 4h column. */
export const PAPER_PAIRS = new Set([
  'btc:5m',
  'btc:15m',
  'eth:5m',
  'eth:15m',
])

export const PAPER_UNITS = {
  'btc:5m': 'pmbot-paper-5m',
  'btc:15m': 'pmbot-paper',
  'eth:5m': 'pmbot-paper-eth-5m',
  'eth:15m': 'pmbot-paper-eth',
}

/** @param {string} asset @param {string} hz */
export function pairKey(asset, hz) {
  return `${String(asset || '').toLowerCase()}:${String(hz || '').toLowerCase()}`
}

/** @param {string} key */
export function parsePairKey(key) {
  const [asset, hz] = String(key || 'btc:15m').toLowerCase().split(':')
  const a = ASSETS.includes(asset) ? asset : 'btc'
  const h = hz === '5m' || hz === '15m' ? hz : '15m'
  return { asset: a, hz: h, key: `${a}:${h}` }
}

/** @param {string} asset @param {string} hz */
export function isPaperPair(asset, hz) {
  return PAPER_PAIRS.has(pairKey(asset, hz))
}

/**
 * Normalize strategies array from a cell-like object.
 * @param {any} cell
 * @returns {any[]}
 */
export function strategiesOf(cell) {
  if (!cell) return []
  return Array.isArray(cell.strategies) ? cell.strategies : []
}

/**
 * Normalize combos array from a cell-like object.
 * @param {any} cell
 * @returns {any[]}
 */
export function combosOf(cell) {
  if (!cell) return []
  return Array.isArray(cell.combos) ? cell.combos : []
}

/**
 * Build cellMap from multicoin snap (+ last-resort BTC dual roster only).
 * Always derive n_strat / n_combo from the actual arrays (ignore lying API zeros).
 * @param {any} snap
 * @returns {Record<string, any>}
 */
export function buildCellMap(snap) {
  const multicoin = (snap && snap.multicoin) || {}
  const cells = Array.isArray(multicoin.cells) ? multicoin.cells : []
  /** @type {Record<string, any>} */
  const m = {}
  for (const c of cells) {
    const asset = String(c.asset || c.coin || '').toLowerCase()
    const horizon = String(c.horizon || c.tf || '').toLowerCase()
    if (!asset || !horizon) continue
    if (horizon === '4h' || horizon === '1h') continue
    let strategies = strategiesOf(c)
    const combos = combosOf(c)
    // API cell present but empty roster: BTC dual only (never inherit BTC onto alts).
    if (
      !strategies.length &&
      asset === 'btc' &&
      snap &&
      snap.strategies &&
      Array.isArray(snap.strategies[horizon])
    ) {
      strategies = snap.strategies[horizon]
    }
    m[`${asset}:${horizon}`] = {
      ...c,
      asset,
      horizon,
      strategies,
      combos,
      // Counts always from arrays — same source as the panel list.
      n_strat: strategies.length,
      n_combo: combos.length,
      status: 'ВКЛ',
      paper: true,
      clickable: true,
      disabled: false,
    }
  }
  for (const key of PAPER_PAIRS) {
    if (m[key]) continue
    const [asset, horizon] = key.split(':')
    // Last-resort BTC roster from dual snap only when multicoin cell missing entirely.
    // Alts MUST stay empty — never inherit BTC list (that was the «stuck on BTC» lie).
    const dual =
      asset === 'btc' && snap && snap.strategies && Array.isArray(snap.strategies[horizon])
        ? snap.strategies[horizon]
        : []
    m[key] = {
      asset,
      horizon,
      paper: true,
      clickable: true,
      disabled: false,
      status: 'ВКЛ',
      unit: PAPER_UNITS[key],
      strategies: dual,
      combos: [],
      n_strat: dual.length,
      n_combo: 0,
      note: 'catalog paper',
    }
  }
  return m
}

/**
 * Strategies for the selected pair — never fall back to another coin's roster.
 * Panel + cell badges both use this (or its .length).
 * @param {Record<string, any>} cellMap
 * @param {string} selKey
 */
export function stratRowsFor(cellMap, selKey) {
  const { key } = parsePairKey(selKey)
  const cell = (cellMap && cellMap[key]) || null
  return strategiesOf(cell)
}

/**
 * @param {Record<string, any>} cellMap
 * @param {string} selKey
 */
export function comboRowsFor(cellMap, selKey) {
  const { key } = parsePairKey(selKey)
  const cell = (cellMap && cellMap[key]) || null
  return combosOf(cell)
}

/**
 * Reactive-friendly counts for every paper pair — same builder as the panel.
 * @param {Record<string, any>} cellMap
 * @returns {Record<string, { n_strat: number, n_combo: number, balance: number|null, win_rate: number|null, phase: string }>}
 */
export function countsByPair(cellMap) {
  /** @type {Record<string, { n_strat: number, n_combo: number, balance: number|null, win_rate: number|null, phase: string }>} */
  const out = {}
  for (const key of PAPER_PAIRS) {
    const cell = (cellMap && cellMap[key]) || null
    const bal = cell && cell.balance != null ? Number(cell.balance) : null
    out[key] = {
      n_strat: stratRowsFor(cellMap, key).length,
      n_combo: comboRowsFor(cellMap, key).length,
      balance: Number.isFinite(bal) ? bal : null,
      win_rate:
        cell && cell.win_rate != null && Number.isFinite(Number(cell.win_rate))
          ? Number(cell.win_rate)
          : null,
      phase: (cell && cell.phase) || '—',
    }
  }
  return out
}
